import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Input,
  Result,
  Space,
  Spin,
  Switch,
  Tag,
  Typography,
  message,
} from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  useGetConciergeOfferQuery,
  useSaveConciergeOfferMutation,
} from '../services/nestjs-base-api';
import { BrandLogo } from '../components/BrandLogo';
import { ThankYouPreview } from '../components/Survey/ThankYouPreview';
import { useAuth } from '../hooks/useAuth';

const { Title, Text, Paragraph } = Typography;

const AMBER_GRADIENT = 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)';
const NAVY = '#18216d';

/**
 * SYR-105 phase 2 — where a brand sets their discount code.
 *
 * Deliberately its own destination with no app chrome. A contact arriving here
 * came from the pack email and is probably on their second ever visit to the
 * platform, so this asks for a few things and gets out of the way. Sending them
 * into the survey editor instead would technically work and would lose most of
 * them: the settings they need are several clicks deep in a wizard built for
 * someone who already knows the product.
 *
 * Everything here is optional. The code, the shop button and the review button
 * are three independent choices, marked as such, because a brand-side discount
 * sign-off is exactly what has stalled otherwise-willing accounts before.
 */
export const SurveyOfferPage: React.FC = () => {
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [code, setCode] = useState('');
  const [offer, setOffer] = useState('');
  const [terms, setTerms] = useState('');
  const [destination, setDestination] = useState('');
  const [showCode, setShowCode] = useState(false);
  const [showDestination, setShowDestination] = useState(true);
  const [review, setReview] = useState('');
  const [showReview, setShowReview] = useState(false);
  const [headline, setHeadline] = useState('');
  const [subheadline, setSubheadline] = useState('');
  const [destinationLabel, setDestinationLabel] = useState('');
  const [reviewLabel, setReviewLabel] = useState('');
  const [saved, setSaved] = useState(false);

  const contextQuery = useGetConciergeOfferQuery(
    { jwtToken: user?.jwtToken ?? '', token },
    { skip: !user?.jwtToken || !token },
  );
  const [save, saveState] = useSaveConciergeOfferMutation();
  const context = contextQuery.data;

  useEffect(() => {
    if (!context) return;
    setShowCode(context.showReward ?? Boolean(context.discountCode));
    setCode(context.discountCode ?? '');
    setOffer(context.offerDescription ?? context.recommendedOffer ?? '');
    setTerms(context.offerTerms ?? '');
    setDestination(context.destinationUrl ?? '');
    setReview(context.reviewUrl ?? '');
    setShowDestination(context.showDestinationButton ?? Boolean(context.destinationUrl));
    setShowReview(context.showReviewButton ?? false);
    setHeadline(context.completionTitle ?? '');
    setSubheadline(context.completionSubtitle ?? '');
    setDestinationLabel(context.destinationButtonText ?? '');
    setReviewLabel(context.reviewButtonText ?? '');
  }, [context]);

  // Bounce to sign-in keeping the token, so they land back here rather than on
  // a dashboard wondering what happened. The shared ProtectedLayout drops the
  // current path, which would lose the link entirely.
  if (!user) {
    const back = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/sign-in?redirect=${back}`} replace />;
  }

  if (!token) {
    return (
      <Shell>
        <Result
          status="warning"
          title={<FormattedMessage id="offer.noToken.title" />}
          subTitle={<FormattedMessage id="offer.noToken.body" />}
        />
      </Shell>
    );
  }

  if (contextQuery.isLoading) {
    return (
      <Shell>
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      </Shell>
    );
  }

  if (contextQuery.error) {
    const status = (contextQuery.error as any)?.status;
    return (
      <Shell>
        <Result
          status="error"
          title={
            <FormattedMessage
              id={status === 403 ? 'offer.wrongAccount.title' : 'offer.expired.title'}
            />
          }
          subTitle={
            <FormattedMessage
              id={status === 403 ? 'offer.wrongAccount.body' : 'offer.expired.body'}
            />
          }
        />
      </Shell>
    );
  }

  if (saved) {
    return (
      <Shell>
        <Result
          icon={<CheckCircleFilled style={{ color: '#22C55E', fontSize: 56 }} />}
          title={<FormattedMessage id="offer.done.title" />}
          subTitle={
            code ? (
              <FormattedMessage id="offer.done.withCode" values={{ code }} />
            ) : (
              <FormattedMessage id="offer.done.withoutCode" />
            )
          }
          extra={
            <Space direction="vertical" size={10} style={{ width: '100%' }}>
              {/* The dashboard is the primary move on purpose. A brand that
                  never comes back to look is functionally the same as one that
                  never sent, so this is the moment to pull them in rather than
                  leave them on a dead end. */}
              <Button
                type="primary"
                size="large"
                onClick={() => navigate('/dashboard')}
                style={{
                  background: AMBER_GRADIENT,
                  border: 'none',
                  height: 48,
                  borderRadius: 10,
                  fontWeight: 600,
                  paddingInline: 28,
                }}
              >
                <FormattedMessage id="offer.done.goToDashboard" />
              </Button>
              <Button type="link" onClick={() => setSaved(false)}>
                <FormattedMessage id="offer.done.changeSomething" />
              </Button>
            </Space>
          }
        />
      </Shell>
    );
  }

  const handleSave = async () => {
    try {
      const result = await save({
        jwtToken: user.jwtToken!,
        token,
        payload: {
          discountCode: code.trim() || null,
          offerDescription: offer.trim() || null,
          offerTerms: terms.trim() || null,
          showReward: showCode,
          destinationUrl: destination.trim() || null,
          reviewUrl: review.trim() || null,
          showDestinationButton: showDestination,
          showReviewButton: showReview,
          destinationButtonText: destinationLabel.trim() || null,
          reviewButtonText: reviewLabel.trim() || null,
          completionTitle: headline.trim() || null,
          completionSubtitle: subheadline.trim() || null,
        },
      }).unwrap();
      message.success(result.message);
      setSaved(true);
    } catch (error: any) {
      message.error(
        error?.data?.message || intl.formatMessage({ id: 'offer.saveFailed' }),
      );
    }
  };

  return (
    <Shell>
      <Title level={3} style={{ marginBottom: 4, color: NAVY, fontWeight: 700 }}>
        <FormattedMessage
          id="offer.title"
          values={{ brand: context?.brandName ?? '' }}
        />
      </Title>
      <Paragraph style={{ color: '#6b7280', fontSize: 15 }}>
        <FormattedMessage id="offer.intro" />
      </Paragraph>

      <Space direction="vertical" size={20} style={{ width: '100%' }}>
        <Section
          title={<FormattedMessage id="offer.headline.title" />}
          hint={<FormattedMessage id="offer.headline.hint" />}
        >
          <Field label={<FormattedMessage id="offer.headline.titleLabel" />}>
            <Input
              size="large"
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
              placeholder="Thanks, that is really helpful"
            />
          </Field>
          <Field label={<FormattedMessage id="offer.headline.subtitleLabel" />}>
            <Input.TextArea
              rows={2}
              value={subheadline}
              onChange={(e) => setSubheadline(e.target.value)}
              placeholder="We read every response."
            />
          </Field>
        </Section>

        <Section
          title={<FormattedMessage id="offer.code.title" />}
          optional
          hint={<FormattedMessage id="offer.code.hint" />}
          toggle={
            <Switch
              checked={showCode}
              onChange={(next) => {
                setShowCode(next);
                // Swap our own default label to match, but never overwrite a
                // label the brand has written themselves.
                const useCode = intl.formatMessage({ id: 'offer.preview.useCode' });
                const visit = intl.formatMessage({ id: 'offer.preview.visit' });
                setDestinationLabel((current) => {
                  if (next && (!current.trim() || current === visit)) return useCode;
                  if (!next && current === useCode) return visit;
                  return current;
                });
              }}
              aria-label={intl.formatMessage({ id: 'offer.code.title' })}
            />
          }
        >
          {showCode && (
          <>
          <Field label={<FormattedMessage id="offer.code.codeLabel" />}>
            <Input
              size="large"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="THANKYOU15"
            />
          </Field>
          <Field
            label={<FormattedMessage id="offer.code.offerLabel" />}
            hint={<FormattedMessage id="offer.code.offerHint" />}
          >
            <Input
              size="large"
              value={offer}
              onChange={(e) => setOffer(e.target.value)}
              placeholder={intl.formatMessage({ id: 'offer.code.offerPlaceholder' })}
            />
          </Field>
          <Field
            label={<FormattedMessage id="offer.code.termsLabel" />}
            hint={<FormattedMessage id="offer.code.termsHint" />}
          >
            <Input.TextArea
              rows={2}
              value={terms}
              onChange={(e) => setTerms(e.target.value)}
              placeholder={intl.formatMessage({ id: 'offer.code.termsPlaceholder' })}
            />
          </Field>
          </>
          )}
        </Section>

        <Section
          title={<FormattedMessage id="offer.destination.title" />}
          optional
          hint={<FormattedMessage id="offer.destination.hint" />}
          toggle={
            <Switch
              checked={showDestination}
              onChange={setShowDestination}
              aria-label={intl.formatMessage({ id: 'offer.destination.title' })}
            />
          }
        >
          {showDestination && (
            <>
              <Field label={<FormattedMessage id="offer.destination.urlLabel" />}>
                <Input
                  size="large"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  placeholder="https://yourstore.com"
                />
              </Field>
              <Field label={<FormattedMessage id="offer.destination.labelLabel" />}>
                <Input
                  size="large"
                  value={destinationLabel}
                  onChange={(e) => setDestinationLabel(e.target.value)}
                  placeholder={intl.formatMessage({
                    id:
                      showCode && code.trim()
                        ? 'offer.preview.useCode'
                        : 'offer.preview.visit',
                  })}
                />
              </Field>
            </>
          )}
        </Section>

        <Section
          title={<FormattedMessage id="offer.review.title" />}
          optional
          hint={<FormattedMessage id="offer.review.hint" />}
          toggle={
            <Switch
              checked={showReview}
              onChange={setShowReview}
              aria-label={intl.formatMessage({ id: 'offer.review.title' })}
            />
          }
        >
          {showReview && (
            <>
              <Field label={<FormattedMessage id="offer.review.urlLabel" />}>
                <Input
                  size="large"
                  value={review}
                  onChange={(e) => setReview(e.target.value)}
                  placeholder="https://g.page/r/..."
                />
              </Field>
              <Field label={<FormattedMessage id="offer.review.labelLabel" />}>
                <Input
                  size="large"
                  value={reviewLabel}
                  onChange={(e) => setReviewLabel(e.target.value)}
                  placeholder={intl.formatMessage({ id: 'offer.preview.review' })}
                />
              </Field>
            </>
          )}
        </Section>

        <Section title={<FormattedMessage id="offer.preview.title" />}>
          <ThankYouPreview
            headline={headline.trim() || intl.formatMessage({ id: 'offer.preview.thanks' })}
            subtitle={subheadline.trim() || null}
            theme={context?.theme ?? null}
            code={showCode ? code : ''}
            offer={offer}
            terms={terms}
            showDestination={showDestination && Boolean(destination.trim())}
            showReview={showReview && Boolean(review.trim())}
            destinationLabel={
              destinationLabel.trim() ||
              intl.formatMessage({
                id:
                  showCode && code.trim()
                    ? 'offer.preview.useCode'
                    : 'offer.preview.visit',
              })
            }
            reviewLabel={
              reviewLabel.trim() || intl.formatMessage({ id: 'offer.preview.review' })
            }
            destinationUrl={destination}
            reviewUrl={review}
            onBadLink={() =>
              message.warning(intl.formatMessage({ id: 'offer.preview.badLink' }))
            }
          />
        </Section>

        {context && context.surveyCount > 1 && (
          <Alert
            type="info"
            showIcon
            message={
              <FormattedMessage
                id="offer.appliesToAll"
                values={{ count: context.surveyCount }}
              />
            }
          />
        )}

        <Button
          type="primary"
          size="large"
          block
          loading={saveState.isLoading}
          onClick={handleSave}
          style={{
            background: AMBER_GRADIENT,
            border: 'none',
            height: 52,
            borderRadius: 10,
            fontWeight: 600,
            fontSize: 16,
            boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
          }}
        >
          <FormattedMessage id="offer.save" />
        </Button>
      </Space>
    </Shell>
  );
};

/** Bare centred shell. No sidebar, no nav, nothing to wander into. */
const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)',
      padding: '40px 16px 64px',
    }}
  >
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      <div style={{ marginBottom: 28 }}>
        <BrandLogo size={44} fontSize={22} />
      </div>
      <div
        style={{
          background: '#fff',
          borderRadius: 16,
          padding: '32px 28px',
          boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
          border: '1px solid #e9edf3',
        }}
      >
        {children}
      </div>
    </div>
  </div>
);

/** A titled block. `optional` is stated as a tag, not buried in the prose. */
const Section: React.FC<{
  title: React.ReactNode;
  hint?: React.ReactNode;
  optional?: boolean;
  toggle?: React.ReactNode;
  children?: React.ReactNode;
}> = ({ title, hint, optional, toggle, children }) => (
  <div
    style={{
      border: '1px solid #e9edf3',
      borderRadius: 12,
      padding: 20,
      background: '#fcfdfe',
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        marginBottom: hint ? 6 : 12,
      }}
    >
      <Space size={8}>
        <Text strong style={{ fontSize: 16, color: NAVY }}>
          {title}
        </Text>
        {optional && (
          <Tag style={{ borderRadius: 999, fontSize: 11, margin: 0 }}>
            <FormattedMessage id="offer.optional" />
          </Tag>
        )}
      </Space>
      {toggle}
    </div>
    {hint && (
      <Paragraph style={{ color: '#6b7280', fontSize: 13, marginBottom: 14 }}>
        {hint}
      </Paragraph>
    )}
    <Space direction="vertical" size={14} style={{ width: '100%' }}>
      {children}
    </Space>
  </div>
);

const Field: React.FC<{
  label: React.ReactNode;
  hint?: React.ReactNode;
  children: React.ReactNode;
}> = ({ label, hint, children }) => (
  <div>
    <Text strong style={{ fontSize: 13, color: '#374151' }}>
      {label}
    </Text>
    {hint && (
      <div>
        <Text style={{ color: '#9ca3af', fontSize: 12 }}>{hint}</Text>
      </div>
    )}
    <div style={{ marginTop: 5 }}>{children}</div>
  </div>
);

export default SurveyOfferPage;
